// ============================================================================
// AiPipelineStatus
// ----------------------------------------------------------------------------
// The QuoteMaker top-nav AI-pipeline LIVE indicator (v5.19.24, split 2026-05-06).
// Renders one of three things depending on pipeline state:
//   • nothing (null) when idle and no just-cancelled toast is showing;
//   • a short-lived green "AI pipeline cancelled" toast right after a STOP;
//   • the pulsing orange "AI · <what's running>" pill while any AI job is in
//     flight (analysis / summary / work-method / photo allocation / quote gen).
// The STOP button that cancels the pipeline lives beside this in App's action
// cluster (a separate element), so this is a pure status indicator.
//
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar sub-slice 3): extracted VERBATIM
// from App.jsx's top-nav render (behaviour-preserving; only type annotations,
// this header, and the null-safe `runningParts[0]?.` access required by
// `noUncheckedIndexedAccess` added — behaviourally identical, no `!`/`as`). The
// component encapsulates the whole null/toast/pill decision (the original inline
// IIFE returned `null` too), so App renders it unconditionally. No pipeline state
// moves in — the five booleans + the cancel-toast object arrive as props.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';

interface AiPipelineStatusProps {
    /** Risk analysis is running. */
    isAnalyzing: boolean;
    /** Project summary generation is running. */
    isSummarizing: boolean;
    /** Quote generation is running. */
    isGenerating: boolean;
    /** Photo allocation is running. */
    photoAssignBusy: boolean;
    /** Work-method (WMS) generation is running. */
    projectWMSGenerating: boolean;
    /** The just-cancelled toast (`{ message }`) or null. Shows briefly after STOP. */
    aiPipelineCancelToast: { message: string } | null;
}

export function AiPipelineStatus({
    isAnalyzing,
    isSummarizing,
    isGenerating,
    photoAssignBusy,
    projectWMSGenerating,
    aiPipelineCancelToast,
}: AiPipelineStatusProps) {
    const aiPipelineActive = isAnalyzing
        || isSummarizing
        || isGenerating
        || photoAssignBusy
        || projectWMSGenerating;
    if (!aiPipelineActive && !aiPipelineCancelToast) return null;
    if (aiPipelineCancelToast && !aiPipelineActive) {
        return (
            <div
                role="status"
                aria-live="polite"
                className="qm-ai-pipeline-toast"
                style={{
                    height: '30px',
                    padding: '0 10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderRadius: '6px',
                    background: 'rgba(16,185,129,0.18)',
                    border: '1px solid rgba(52,211,153,0.55)',
                    color: '#6ee7b7',
                    fontSize: '10.5px',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    whiteSpace: 'nowrap',
                }}
            >
                <Icons.Check size={13} aria-hidden="true" />
                <span>{aiPipelineCancelToast.message}</span>
            </div>
        );
    }
    // Build a compact, honest list of what's running
    // so the user knows what they're about to cancel.
    const runningParts: string[] = [];
    if (isAnalyzing) runningParts.push('analysis');
    if (isSummarizing) runningParts.push('summary');
    if (projectWMSGenerating) runningParts.push('work method');
    if (photoAssignBusy) runningParts.push('photo allocation');
    if (isGenerating) runningParts.push('quote gen');
    const runningLabel = runningParts.length === 1
        ? runningParts[0]?.toUpperCase()
        : `${runningParts.length} JOBS`;
    const runningTooltip = runningParts.length > 0
        ? `In flight: ${runningParts.join(', ')}. Click STOP beside this pill to cancel.`
        : 'AI pipeline working';
    return (
        <div
            role="status"
            aria-live="polite"
            aria-label="AI pipeline status"
            className="qm-ai-pipeline-pill"
            style={{
                height: '30px',
                padding: '0 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '6px',
                background: 'linear-gradient(180deg, color-mix(in srgb, var(--qm-accent) 22%, transparent), color-mix(in srgb, var(--qm-accent) 12%, transparent))',
                border: '1.5px solid color-mix(in srgb, var(--qm-accent) 55%, transparent)',
                boxShadow: '0 0 0 0 color-mix(in srgb, var(--qm-accent) 45%, transparent)',
                animation: 'qmAiPipelinePulse 1.6s ease-in-out infinite',
            }}
            title={runningTooltip}
        >
            <span
                aria-hidden="true"
                style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--qm-accent)',
                    boxShadow: '0 0 6px color-mix(in srgb, var(--qm-accent) 90%, transparent)',
                    animation: 'qmAiPipelineDot 0.9s ease-in-out infinite',
                }}
            />
            <span style={{
                color: '#fbbf24',
                fontSize: '10.5px',
                fontWeight: 900,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                whiteSpace: 'nowrap',
            }}>AI<span className="qm-ai-pill-detail"> · {runningLabel}</span></span>
        </div>
    );
}
